import { Box, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import Checkbox from 'components/checkbox';
import FilterSidebar from 'components/filter-sidebar';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import Tooltip from 'components/tooltip';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import Lock from 'views/production-systems/features/farm-overview/assets/Lock';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { Pond, PondStatus } from 'views/production-systems/types/pond.type';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { getPonds, getZones } from 'views/production-systems/api/productionSystems';
import { useGet } from 'hooks/use-get';
import _ from 'underscore';
import { calculateBiomass } from 'views/production-systems/utils/farm-overview';

const PAGE_NAME_ID = 'productionSystems.farmOverview.transfer';

type Props = {
  destinationPonds: Pond[];
  sourcePond: Pond | undefined;
  setDestinationPonds: (ponds: Pond[]) => void;
};

type ParamTypes = {
  globalSiteId: string;
};

const FilterBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  [theme.breakpoints.up('xs')]: {
    flexWrap: 'wrap',
    gap: 16,
  },
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
    gap: 16,
  },
}));

type SortablePond = Pond & {
  disabled: boolean;
  biomass: number;
};

const TransferFirstStep: React.FC<Props> = ({ destinationPonds, sourcePond, setDestinationPonds }) => {
  const intl = useIntl();
  const params = useParams<ParamTypes>();
  const theme = useTheme();
  const { registrationDate } = usePondsState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #region STATES

  const [searchValue, setSearchValue] = useState<string>('');
  const [zoneId, setZoneId] = useState<string>('');
  const [showActivePonds, setShowActivePonds] = useState<boolean>(true);
  const [showInactivePonds, setShowInactivePonds] = useState<boolean>(true);
  const [ponds, setPonds] = useState<SortablePond[]>([]);

  // #region FETCH DATA

  const { data: zones } = useGet(['getZones', params.globalSiteId ?? ''], ({ queryKey }) => getZones(String(queryKey[1])), {
    enabled: true,
  });

  const { data: pondState, isFetching } = useGet(
    ['getPonds', params.globalSiteId, registrationDate.format('YYYY-MM-DD')],
    ({ queryKey }) => getPonds(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!params.globalSiteId,
    }
  );

  useEffect(() => {
    if (!pondState) return;

    setPonds(
      _.chain(pondState.ponds)
        .filter((pond) => pond.pondId !== sourcePond?.pondId && pond.allowStocking)
        .map((pond) => ({ ...pond, disabled: !!pond.lockCreatedBy, biomass: calculateBiomass(pond.currentMw, pond.currentQty) }))
        .sortBy('pondName')
        .value()
    );
  }, [pondState, sourcePond?.pondId]);

  // #endregion

  // #region HELPERS
  const getIcon = (type: boolean, sx?: CSSProperties) => {
    return type ? <Active sx={sx} /> : <Inactive style={sx} />;
  };

  const handleDestinationPonds = (value: Pond, checked: boolean) => {
    setDestinationPonds(checked ? destinationPonds.concat([value]) : destinationPonds.filter((s) => s !== value));
  };

  const resetStates = () => {
    setZoneId('');
    setSearchValue('');
    setShowActivePonds(true);
    setShowInactivePonds(true);
  };

  // #region RENDERS

  const renderPonds = useMemo(() => {
    let rows: SortablePond[] = [];

    rows = _.chain(ponds)
      .filter((pond) => (showInactivePonds ? true : pond.pondStatus === PondStatus.active))
      .filter((pond) => (showActivePonds ? true : pond.pondStatus === PondStatus.inactive))
      .filter((pond) => (!zoneId ? true : pond.zoneId === zoneId))
      .filter((pond) => pond.pondName.toLowerCase().includes(searchValue.toLowerCase()))
      .value();

    return rows;
  }, [showInactivePonds, showActivePonds, zoneId, ponds, searchValue]);

  return (
    <Box>
      <FlexBox>
        <FlexBox flexDirection={isMobile ? 'column' : 'row'}>
          <TextField
            label={intl.formatMessage({ id: 'productionSystems.selectedDate' })}
            variant="outlined"
            dark
            type="text"
            disabled
            value={registrationDate.format('DD/MM/YYYY')}
          />
          <TextField
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.fields.sourcePond` })}
            dark
            type="text"
            value={sourcePond?.pondName}
            disabled
          />
          <Tooltip title={sourcePond?.cycleName}>
            <TextField
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.fields.cycleName` })}
              variant="outlined"
              dark
              type="text"
              value={sourcePond?.cycleName}
              disabled
            />
          </Tooltip>
        </FlexBox>

        <Paper sx={{ border: '1px solid' }}>
          <Typography mt={4} mb={4}>
            <FormattedMessage id={`${PAGE_NAME_ID}.selectDestinationPond`} />
          </Typography>
          <FilterSidebar
            dark
            sx={{
              justifyContent: 'space-between',
            }}
            showClearFilter={!!zoneId || !!searchValue || !showActivePonds || !showInactivePonds}
            clearFilters={resetStates}>
            <FilterBox>
              <Select
                dark
                label={intl.formatMessage({ id: `productionSystems.farmOverview.filters.zone` })}
                value={zoneId}
                onChange={(event) => {
                  setZoneId(String(event.target.value));
                }}>
                {zones?.map((zone) => (
                  <RadioSelectItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </RadioSelectItem>
                ))}
              </Select>
              <TextField
                dark
                value={searchValue}
                label={intl.formatMessage({ id: 'general.search' })}
                onChange={(event) => setSearchValue(event.target.value)}
                endAdornment={<Search size={18} />}
              />
            </FilterBox>
            <FilterBox>
              <Switch
                size="small"
                dark
                label={intl.formatMessage({ id: `productionSystems.farmOverview.filters.showActive` })}
                value={showActivePonds}
                onChange={() => setShowActivePonds((prev) => !prev)}
              />

              <Switch
                size="small"
                dark
                label={intl.formatMessage({ id: `productionSystems.farmOverview.filters.showInactive` })}
                value={showInactivePonds}
                onChange={() => setShowInactivePonds((prev) => !prev)}
              />
            </FilterBox>
          </FilterSidebar>
          <Box overflow={'auto'}>
            <List
              dark
              sx={{ height: renderPonds.length > 20 ? '50vh' : 'auto' }}
              rows={ponds}
              sort={setPonds}
              options={[
                { id: 'checked', label: '', autoWidth: true },
                { id: 'pondName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
                { id: 'zoneName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.zone` }) },
                { id: 'status', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.status` }), align: 'center' },
                {
                  id: 'currentQty',
                  label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.noShrimps` }),
                  align: 'right',
                },
                { id: 'currentMw', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.meanWeight` }), align: 'right' },
                { id: 'biomass', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.biomass` }), align: 'right' },
                {
                  id: 'activeInactiveDays',
                  label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.days` }),
                  align: 'right',
                  sx: { whiteSpace: 'nowrap' },
                },
              ]}
              isLoading={isFetching}
              noResult={{
                show: !isFetching && !renderPonds.length,
                text: intl.formatMessage({ id: 'general.noResults' }),
              }}>
              {renderPonds?.map((pond, idx) => {
                return (
                  <ListRow key={idx} sx={{ backgroundColor: `${theme.palette.lightBlue.o25} !important` }}>
                    <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                      <Checkbox
                        dark
                        hiddenLabel
                        disabled={pond.disabled}
                        checkedIcon={pond.disabled && <Lock />}
                        checked={pond.disabled || destinationPonds.includes(pond)}
                        onChange={(_, checked) => handleDestinationPonds(pond, checked)}
                      />
                    </ListCell>
                    <ListCell>{pond.pondName}</ListCell>
                    <ListCell>{pond.zoneName}</ListCell>
                    <ListCell align="center">
                      {getIcon(pond.pondStatus === PondStatus.active, { verticalAlign: 'middle' })}
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber value={pond.currentQty} />
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber maximumFractionDigits={2} value={pond.currentMw} />
                    </ListCell>
                    <ListCell align={'right'}>
                      <FormattedNumber maximumFractionDigits={2} value={pond.biomass} />
                    </ListCell>
                    <ListCell align="right">{pond.activeInactiveDays}</ListCell>
                  </ListRow>
                );
              })}
            </List>
          </Box>
        </Paper>
      </FlexBox>
    </Box>
  );
};

export default TransferFirstStep;
